import { useTranslation } from 'react-i18next'
import { FormikProvider } from 'formik'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  DialogActions,
  Typography,
  Chip,
} from '@mui/material'

import { Cancel, Close } from '@mui/icons-material'
import { useBonusDialog } from '@/views/bonus-engine/hooks'
import { CurrencyCodeEnum, DialogsEnum } from '@/enums'

import InputFormikAutocomplete from '@/views/components/input-formik-autocomplete/index.component'
import InputFormikField from '@/views/components/input-formik-field/index.component'
import { useBrandsForm } from '@/views/users/hooks/use-brands-form.hook'

import keys from '@/utils/keys'

export const BrandsFormComponent = () => {
  const { t } = useTranslation()

  const { dialog, toggleDialog } = useBonusDialog()

  const { formikBrand, currentBrand, handleSaveBrand, setCurrentBrand } =
    useBrandsForm()

  const handleCloseDialog = () => {
    toggleDialog(false, DialogsEnum.DIALOG_BRAND_FORM)
    setCurrentBrand()
  }

  const currenciesOptions = [
    ...keys(CurrencyCodeEnum).map((index) => {
      return index
    }),
  ]

  return (
    <Dialog
      open={dialog.open && dialog.id === DialogsEnum.DIALOG_BRAND_FORM}
      aria-labelledby="dialog-games-form"
      fullWidth
      sx={{ '& .MuiPaper-root': { width: '500px' } }}
    >
      <DialogTitle id="dialog-games-form" sx={{ padding: '16px 24px 20px' }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box>{t('brand')}</Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            size="small"
          >
            <Close fontSize="inherit" />
          </IconButton>
        </Box>
      </DialogTitle>
      <FormikProvider value={formikBrand}>
        <DialogContent>
          <Box overflow="hidden">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <InputFormikField
                  name="name"
                  label={t('labels.name')}
                  sx={{ marginTop: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <InputFormikField
                  name="siteId"
                  label={t('labels.siteId')}
                  sx={{ marginTop: 1 }}
                  disabled={currentBrand !== null}
                />
              </Grid>
              {process.env.REACT_APP_STAGE !== 'prod' && (
                <Grid item xs={12}>
                  <InputFormikField
                    name="operatorId"
                    label={`${t('labels.gameStoreId')} ${t('labels.optional')}`}
                    sx={{ marginTop: 1 }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <InputFormikAutocomplete
                  name="currencies"
                  label={t('labels.currencies')}
                  options={currenciesOptions}
                  getOptionLabel={(option: any) =>
                    `${option}: ${t(`currenciesCodes.${option}`)}`
                  }
                  manuallySetValue
                  handleChange={(event: any, newValue: string[]) => {
                    if (event.keyCode !== 8) {
                      let value = newValue
                      if (
                        newValue.findIndex(
                          (index) => index === CurrencyCodeEnum.USD,
                        ) === -1
                      ) {
                        value = [CurrencyCodeEnum.USD, ...value]
                      }
                      formikBrand.setFieldValue('currencies', value)
                    }
                  }}
                  renderOption={(props, option, { selected }) => {
                    return (
                      <Box component="li" {...props}>
                        <Checkbox
                          color="primary"
                          checked={selected || option === CurrencyCodeEnum.USD}
                          disabled={option === CurrencyCodeEnum.USD}
                        />

                        <Typography variant="body2">
                          {`${option}: ${t(`currenciesCodes.${option}`)}`}
                        </Typography>
                      </Box>
                    )
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        deleteIcon={
                          option !== CurrencyCodeEnum.USD ? <Cancel /> : <></>
                        }
                        size="small"
                        label={`${option}: ${t(`currenciesCodes.${option}`)}`}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  chipTags
                  multiple
                  disableClearable={true}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label={t('labels.active')}
                  control={
                    <Checkbox
                      name="enable"
                      checked={formikBrand.values.enable}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        formikBrand.setFieldValue(
                          'enable',
                          !formikBrand.values.enable,
                        )
                      }
                    />
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </FormikProvider>
      <DialogActions>
        <Button onClick={handleCloseDialog} className="red">
          {t('labels.cancel')}
        </Button>
        <Button
          disabled={!(formikBrand.isValid && formikBrand.dirty)}
          variant="contained"
          onClick={handleSaveBrand}
        >
          {currentBrand?.id ? t('labels.update') : t('labels.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
